import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LoadingScreen = ({ onLoadingComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    // Simulate loading progress
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setIsComplete(true);
            setTimeout(() => {
              onLoadingComplete?.();
            }, 500);
          }, 300);
          return 100;
        }
        // Variable speed - faster at start, slower near end
        const increment = prev < 70 ? Math.random() * 8 + 2 : Math.random() * 3 + 1;
        return Math.min(prev + increment, 100);
      });
    }, 100);

    return () => clearInterval(interval);
  }, [onLoadingComplete]);

  return (
    <AnimatePresence>
      {!isComplete && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[9999] bg-black overflow-hidden"
          data-testid="loading-screen"
        >
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src="https://customer-assets.emergentagent.com/job_4c9ec105-7961-43e0-9222-2e0dd1f1cf63/artifacts/t89jukih_Screenshot%202026-02-22%20134730.png"
              alt="Loading"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-black/60" />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          </div>

          {/* Content */}
          <div className="relative z-10 h-full flex flex-col items-center justify-center px-8">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-16"
            >
              <h1 className="text-4xl md:text-5xl font-bold font-['Syne'] text-white tracking-tight">
                Bankki <span className="font-normal text-white/60">Media</span>
              </h1>
            </motion.div>

            {/* Race Track Container */}
            <div className="w-full max-w-4xl relative">
              {/* Track Background */}
              <div className="relative h-24 md:h-32">
                {/* Track Lines */}
                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2">
                  {/* Main track */}
                  <div className="h-16 md:h-20 bg-gray-800/50 rounded-lg border border-gray-700/50 relative overflow-hidden">
                    {/* Track stripes */}
                    <div className="absolute inset-0 flex items-center">
                      {[...Array(20)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="h-1 w-8 bg-white/20 mx-4"
                          initial={{ opacity: 0.2 }}
                          animate={{ opacity: [0.2, 0.5, 0.2] }}
                          transition={{ duration: 1, repeat: Infinity, delay: i * 0.1 }}
                        />
                      ))}
                    </div>
                    
                    {/* Progress bar track */}
                    <motion.div
                      className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-orange-500 via-orange-400 to-yellow-400"
                      style={{ width: `${progress}%` }}
                      transition={{ duration: 0.1 }}
                    />
                  </div>
                </div>

                {/* Finish Line */}
                <div className="absolute right-0 top-1/2 -translate-y-1/2 h-20 md:h-24 w-6 md:w-8 flex flex-col">
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className={`flex-1 flex ${i % 2 === 0 ? '' : 'flex-row-reverse'}`}
                    >
                      <div className="w-1/2 bg-white" />
                      <div className="w-1/2 bg-black" />
                    </div>
                  ))}
                </div>

                {/* McLaren F1 Car */}
                <motion.div
                  className="absolute top-1/2 -translate-y-1/2 z-20"
                  style={{ left: `${Math.min(progress - 5, 92)}%` }}
                  animate={{
                    y: ['-50%', '-52%', '-50%', '-48%', '-50%'],
                  }}
                  transition={{
                    duration: 0.3,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                >
                  {/* F1 Car SVG - McLaren Papaya Orange */}
                  <svg
                    width="80"
                    height="32"
                    viewBox="0 0 80 32"
                    className="md:w-[100px] md:h-[40px]"
                  >
                    {/* Car Body - Main */}
                    <path
                      d="M10 18 L15 12 L55 10 L70 12 L75 16 L75 20 L70 22 L15 22 L10 20 Z"
                      fill="#FF8000"
                      className="drop-shadow-lg"
                    />
                    {/* Nose Cone */}
                    <path
                      d="M70 14 L78 16 L70 18 Z"
                      fill="#FF8000"
                    />
                    {/* Cockpit */}
                    <ellipse cx="35" cy="14" rx="8" ry="4" fill="#1a1a1a" />
                    {/* Halo */}
                    <path
                      d="M30 12 Q35 8 40 12"
                      stroke="#333"
                      strokeWidth="2"
                      fill="none"
                    />
                    {/* Front Wing */}
                    <rect x="68" y="10" width="10" height="2" fill="#1a1a1a" />
                    <rect x="68" y="20" width="10" height="2" fill="#1a1a1a" />
                    {/* Rear Wing */}
                    <rect x="5" y="8" width="12" height="3" fill="#1a1a1a" />
                    <rect x="8" y="11" width="2" height="7" fill="#333" />
                    {/* Front Wheel */}
                    <circle cx="62" cy="20" r="5" fill="#1a1a1a" />
                    <circle cx="62" cy="20" r="3" fill="#333" />
                    {/* Rear Wheel */}
                    <circle cx="18" cy="20" r="5" fill="#1a1a1a" />
                    <circle cx="18" cy="20" r="3" fill="#333" />
                    {/* Blue accent (McLaren detail) */}
                    <rect x="25" y="16" width="20" height="2" fill="#0066CC" />
                    {/* Number */}
                    <text x="45" y="18" fill="white" fontSize="6" fontWeight="bold">4</text>
                  </svg>

                  {/* Exhaust flames when moving fast */}
                  {progress < 95 && (
                    <motion.div
                      className="absolute -left-4 top-1/2 -translate-y-1/2"
                      animate={{ 
                        opacity: [0.8, 0.4, 0.8],
                        scaleX: [1, 1.3, 1],
                      }}
                      transition={{ duration: 0.1, repeat: Infinity }}
                    >
                      <div className="w-6 h-2 bg-gradient-to-l from-orange-500 via-yellow-400 to-transparent rounded-full blur-sm" />
                    </motion.div>
                  )}
                </motion.div>

                {/* Speed lines behind car */}
                {progress > 10 && progress < 95 && (
                  <motion.div
                    className="absolute top-1/2 -translate-y-1/2 z-10"
                    style={{ left: `${Math.max(progress - 25, 0)}%`, width: '15%' }}
                  >
                    {[...Array(3)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="h-px bg-gradient-to-r from-transparent via-white/30 to-transparent my-2"
                        animate={{ opacity: [0, 0.5, 0] }}
                        transition={{ duration: 0.3, repeat: Infinity, delay: i * 0.1 }}
                      />
                    ))}
                  </motion.div>
                )}
              </div>

              {/* Progress Percentage */}
              <motion.div
                className="mt-8 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <span className="text-6xl md:text-7xl font-bold font-['Syne'] text-white tabular-nums">
                  {Math.floor(progress)}
                </span>
                <span className="text-2xl md:text-3xl font-bold text-white/60 ml-1">%</span>
              </motion.div>

              {/* Loading Text */}
              <motion.p
                className="text-center text-white/50 text-sm uppercase tracking-widest mt-4"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                {progress < 100 ? 'Loading Experience...' : 'Ready to Race!'}
              </motion.p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
